// The investigation's own decisions, beside its evidence.
//
// **A change is a compact card that opens the Inbox's drawer** (#869): the column names
// each change the investigation asked for — what, how risky, how long is left — and
// pressing one opens `InboxDrawer` on it, where the full approval card and its identity
// check are. One drawer, not a second copy of it, so the run page answers a change
// exactly as the Inbox does. The row was read in the session's selected context, so the
// drawer is opened in that context and selects nothing. Closing it returns focus to the
// card, through the key the Inbox's rows use (`data-inbox-open`).

import { useEffect, useRef, useState } from "react";
import type { ChangeRow } from "../api/changes";
import type { ContextChoice, SessionResponse } from "../api/session";
import type { RunProjection } from "../features/ops/investigationProjection";
import { timeLeft } from "./ApprovalCard";
import { ConsentCard } from "./ConsentPage";
import { Chip, Notice } from "./chrome";
import { InboxDrawer } from "./InboxDrawer";
import { openKey, type OpenItem } from "./inboxView";
import { useStepUpFactors } from "./StepUp";
import { decisionReceipt, useNow } from "./decisionState";
import type { ConsoleState } from "./consoleState";
import type { ConsoleActions } from "./useConsole";

/** The context the console's reads were taken in: the one that answers what they list. */
function selected(session: SessionResponse): ContextChoice | null {
  if (session.workspace_id !== null) return { workspace_id: session.workspace_id };
  if (session.org_id !== null) return { org_id: session.org_id };
  return null;
}

const text = (value: unknown): string => (typeof value === "string" ? value : "");

/** One change, compact: pressing it opens the drawer that answers it. */
function ProposalCard({ change, onOpen }: { change: ChangeRow; onOpen: () => void }) {
  const now = useNow();
  const risk = text(change.request.risk);
  return (
    <button
      type="button"
      className="proposal-card"
      data-testid="proposal-card"
      data-inbox-open={openKey({ kind: "change_approval", id: change.change_id })}
      aria-haspopup="dialog"
      onClick={onOpen}
    >
      <span className="proposal-card__title">{text(change.request.title) || "A write the investigation asked for"}</span>
      <span className="proposal-card__meta">
        {risk === "" ? null : <Chip>{risk} risk</Chip>}
        <span>{timeLeft(change.expires_at, now)}</span>
      </span>
      {change.account_connected === false ? (
        // An approval on an account that is not connected is refused (#1123): said here, before
        // the card is opened, as the full card says it in place of Approve.
        <span className="proposal-card__meta">
          Its AWS account is no longer connected, so it cannot be approved. It can still be declined.
        </span>
      ) : null}
    </button>
  );
}

/** Reuses queue controls; only the server's run/request identities select a card. */
export function RunDecisions({
  runId,
  projection,
  session,
  state,
  actions,
}: {
  runId: string;
  projection: RunProjection;
  session: SessionResponse;
  state: ConsoleState;
  actions: ConsoleActions;
}) {
  const refresh = useRef(actions.refreshDecisions);
  refresh.current = actions.refreshDecisions;
  const revision = JSON.stringify([
    projection.requestedAction,
    projection.actions,
    projection.consents,
  ]);
  const previous = useRef(revision);
  useEffect(() => {
    if (previous.current === revision) return;
    previous.current = revision;
    void refresh.current?.();
  }, [revision]);
  const [receipt, setReceipt] = useState<string | null>(null);
  const [open, setOpen] = useState<OpenItem | null>(null);
  // The same reading the Changes and Consent screens take: nothing is offered
  // until it answers (#739).
  const factors = useStepUpFactors(actions.stepUpFactors);
  const changes = state.changes?.filter((row) => row.run_id === runId);
  const consent = state.consent?.requests.filter((row) => row.run_id === runId);
  return (
    <div className="stack decision-requests">
      {receipt ? <Notice>{receipt}</Notice> : null}
      {state.changesError ? (
        <Notice tone="bad">
          Changes unavailable: {state.changesError}{" "}
          <a href="/changes">Read changes again</a>
        </Notice>
      ) : changes === undefined ? (
        <p role="status">Reading proposed changes…</p>
      ) : changes.length === 0 ? (
        <p className="muted">
          No change is awaiting your answer for this investigation.
        </p>
      ) : null}
      {changes?.map((change) => (
        <ProposalCard
          key={change.change_id}
          change={change}
          onOpen={() => setOpen({ kind: "change_approval", id: change.change_id, context: selected(session) })}
        />
      ))}
      {state.consentError ? (
        <Notice tone="bad">
          Consent unavailable: {state.consentError}{" "}
          <a href="/consent">Read consent again</a>
        </Notice>
      ) : consent === undefined ? (
        <p role="status">Reading consent requests…</p>
      ) : consent.length === 0 ? (
        <p className="muted">
          No consent is awaiting your answer for this investigation.
        </p>
      ) : null}
      {consent?.map((row) => (
        <ConsentCard
          key={row.request_id}
          row={row}
          factors={factors}
          beginPasskey={actions.beginPasskey}
          onAnswer={async (...args) => {
            const result = await actions.answerConsent(...args);
            if (result) setReceipt(decisionReceipt(result.outcome));
            return result;
          }}
        />
      ))}
      {open === null ? null : (
        <InboxDrawer
          key={openKey(open)}
          open={open}
          item={
            state.inbox?.groups
              .flatMap((group) => group.items)
              .find((item) => item.kind === open.kind && item.id === open.id) ?? null
          }
          filter={null}
          session={session}
          contextError={state.contextError ?? null}
          sources={{
            consent: state.consent,
            consentError: state.consentError,
            changes: state.changes,
            changesError: state.changesError,
            access: state.access,
            accessError: state.accessError,
          }}
          actions={{
            answerConsent: actions.answerConsent,
            answerChange: async (...args) => {
              const result = await actions.answerChange(...args);
              if (result) setReceipt(decisionReceipt(result.outcome));
              void actions.refreshInbox?.();
              return result;
            },
            answerGrant: actions.answerGrant,
            accept: actions.acceptWaiting,
            stepUpFactors: actions.stepUpFactors,
            beginPasskey: actions.beginPasskey,
          }}
          address={session.user.email}
          onClose={() => setOpen(null)}
        />
      )}
    </div>
  );
}
